import { useEffect, useRef } from 'react';

/** Captures the currently-focused element when `active` becomes `true`
 *  and restores focus to it when `active` becomes `false` or the
 *  component unmounts. Used by Dialog / Drawer / Menu / Select so
 *  keyboard users aren't dropped to `<body>` after a close.
 *
 *  `preventScroll: true` avoids a brief scroll jump on restore when
 *  the previously-focused element is off-screen. */
export function useRestoreFocus(active: boolean): void {
  const previousRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return undefined;
    if (typeof document === 'undefined') return undefined;
    previousRef.current = document.activeElement as HTMLElement | null;
    return () => {
      const prev = previousRef.current;
      if (prev !== null && typeof prev.focus === 'function') {
        try {
          prev.focus({ preventScroll: true });
        } catch {
          /* focus() may throw if element was removed from DOM — ignore */
        }
      }
    };
  }, [active]);
}
